import { Component, OnInit } from '@angular/core';
import {EducationalDataService} from "./educational-data.service";
import {HelptextService} from "../shared/helptext.service";
import {MatSnackBar as MatSnackBar} from "@angular/material/snack-bar";
import {map, switchMap, tap} from "rxjs/operators";
import {combineLatest, of} from "rxjs";
import {DataService} from "../shared/data.service";
import {Validators} from "@angular/forms";
import {LocaleService} from "../locale.service";


@Component({
    selector: 'app-educational-data',
    templateUrl: './educational-data.component.html',
    styleUrls: ['./educational-data.component.css'],
    standalone: false
})
export class EducationalDataComponent implements OnInit {
  informations = [
    {
      fieldName: 'id',
      display_en: 'none',
      display_es: 'none',
      display_fr: 'none',
      display_pt: 'none',
      type: 'n/a',
    },
    {
      fieldName: 'data_year',
      display_en: 'none',
      display_es: 'none',
      display_fr: 'none',
      display_pt: 'none',
      type: 'n/a',
    },
    {
      fieldName: 'district',
      display_en: 'District',
      display_es: 'Distrito',
      display_fr: 'District',
      display_pt: 'Distrito',
      type: 'string',
    },
    {
      fieldName: 'kids_in_primary_age',
      display_en: 'Primary enrolment rate %',
      display_es: 'Tasa de matriculación primaria %',
      display_fr: 'Taux de scolarisation primaire %',
      display_pt: 'Taxa de matrícula primária %',
      type: 'number',
    },
    {
      fieldName: 'completion_rate_in_primary_stage',
      display_en: 'Primary completion rate %',
      display_es: 'Tasa de finalización primaria %',
      display_fr: 'Taux d\'achèvement primaire %',
      display_pt: 'Taxa de conclusão primária %',
      type: 'number',
    },
    {
      fieldName: 'pupils_per_teacher',
      display_en: 'Primary pupils per teacher',
      display_es: 'Alumnos de primaria por maestro',
      display_fr: 'Élèves du primaire par enseignant',
      display_pt: 'Alunos do primário por professor',
      type: 'number',
    },
    {
      fieldName: 'enrolment_rate_in_secondary_stage',
      display_en: 'Secondary enrolment rate %',
      display_es: 'Tasa de matriculación secundaria %',
      display_fr: 'Taux de scolarisation secondaire %',
      display_pt: 'Taxa de matrícula secundária %',
      type: 'number',
    },
    {
      fieldName: 'completion_rate_in_secondary_stage',
      display_en: 'Secondary completion rate %',
      display_es: 'Tasa de finalización secundaria %',
      display_fr: 'Taux d\'achèvement secondaire %',
      display_pt: 'Taxa de conclusão secundária %',
      type: 'number',
    },
  ];
  helptexts: any[] = [];
  districts: any[] = [];

  constructor(
    private eduDataService: EducationalDataService,
    private helptextService: HelptextService,
    private snackbar: MatSnackBar,
    private dataservice: DataService,
    private localeService: LocaleService
  ) {}

  ngOnInit(): void {
    this.localeService.updateLocale()
    this.helptextService
      .getHelptexts('helptexts-educational-data')
      .subscribe((res) => (this.helptexts = res));
    combineLatest([
      this.dataservice.selectedYear,
      this.eduDataService.getEducationalData(),
    ])
      .pipe(
        switchMap(([year, edudata]) => {
          if (edudata.length < 1) {
            console.log('No educational data found, creating initial record for year:', year);
            return of({
              data_year: year.id,
              kids_in_primary_age: '',
              completion_rate_in_primary_stage: '',
              pupils_per_teacher: '',
              enrolment_rate_in_secondary_stage: '',
              completion_rate_in_secondary_stage: '',
              district: '',
            });
          }
          return of(edudata);
        })
      )
      .subscribe((res) => {
        Array.isArray(res) ? (this.districts = res) : (this.districts = [res]);
      });
  }

  onData(formData: any) {
    if (!this.districts) throw new Error('Missing Data');
    console.log('Regional Completion Rate - onData called with:', formData);
    console.log('Regional Completion Rate - stringified:', JSON.stringify(formData, null, 2));

    // For POST: keep required fields, set optional numeric fields to null
    // For PUT: keep all fields including null values
    const cleanedData: any = { ...formData };

    // Remove the id field if it's null (for POST)
    if (cleanedData.id === null) {
      delete cleanedData.id;
    }

    console.log('Cleaned data for API:', JSON.stringify(cleanedData, null, 2));

    if (formData.id == null) {
      console.log('Creating new regional completion rate (POST)');
      this.eduDataService.post(cleanedData).subscribe({
        next: (res) => {
          console.log('POST successful:', res);
          this.snackbar.open('Values updated successfully', undefined, {
            duration: 2500,
          });
          this.districts.pop();
          this.districts.push(res);
        },
        error: (error) => {
          console.error('Failed to create regional completion rate:', error);
          if (error.error?.data) {
            console.error('Validation error details:', error.error.data);
          }
          this.snackbar.open('Error saving data: ' + (error.error?.message || error.message), undefined, {
            duration: 5000,
          });
        }
      });
    } else {
      console.log('Updating existing regional completion rate (PUT)');
      this.eduDataService.updateData(cleanedData).subscribe({
        next: (data) => {
          console.log('PUT successful:', data);
          this.snackbar.open('Values updated successfully', undefined, {
            duration: 2500,
          });
          formData = data;
        },
        error: (error) => {
          console.error('Failed to update regional completion rate:', error);
          if (error.error?.data) {
            console.error('Validation error details:', error.error.data);
          }
          this.snackbar.open('Error saving data: ' + (error.error?.message || error.message), undefined, {
            duration: 5000,
          });
        }
      });
    }
  }

  addNewRegionalCompletionRate() {
    this.dataservice.selectedYear.subscribe((year) => {
      console.log('Adding new regional completion rate for year:', year);
      this.districts.push({
        data_year: year.id,
        kids_in_primary_age: '',
        completion_rate_in_primary_stage: '',
        pupils_per_teacher: '',
        enrolment_rate_in_secondary_stage: '',
        completion_rate_in_secondary_stage: '',
        district: '',
      });
    });
  }

  deleteRegionalCompletionRateDistrict($event: any) {
    this.eduDataService.delete($event.id).subscribe((res: any) => {
      this.districts = this.districts.filter((distr) => distr.id != res.id);
      this.snackbar.open('Deleted successfully', undefined, { duration: 2500 });
    });
  }
}
